/**
 * Cache Management Service for STB Workshop
 * Handles cache freshness and automatic invalidation
 */

const CACHE_EXPIRY_TIME = 30 * 60 * 1000; // 30 minutes in milliseconds
const CACHE_METADATA_KEY = 'stb_cache_metadata';

/**
 * Cache metadata structure:
 * {
 *   orders: { lastSync: timestamp, isStale: boolean },
 *   clients: { lastSync: timestamp, isStale: boolean },
 *   technicians: { lastSync: timestamp, isStale: boolean },
 *   equipment: { lastSync: timestamp, isStale: boolean },
 *   brands: { lastSync: timestamp, isStale: boolean },
 *   lastAppOpen: timestamp
 * }
 */

/**
 * Gets cache metadata from localStorage
 * @returns {Object} Cache metadata
 */
export const getCacheMetadata = () => {
  try {
    const metadata = localStorage.getItem(CACHE_METADATA_KEY);
    return metadata ? JSON.parse(metadata) : {};
  } catch (error) {
    console.error('Error reading cache metadata:', error);
    return {};
  }
};

/**
 * Updates cache metadata in localStorage
 * @param {Object} updates - Metadata updates
 */
export const updateCacheMetadata = (updates) => {
  try {
    const currentMetadata = getCacheMetadata();
    const newMetadata = {
      ...currentMetadata,
      ...updates
    };
    localStorage.setItem(CACHE_METADATA_KEY, JSON.stringify(newMetadata));
  } catch (error) {
    console.error('Error updating cache metadata:', error);
  }
};

/**
 * Records when data was last synced for a specific data type
 * @param {string} dataType - Type of data (orders, clients, etc.)
 */
export const recordDataSync = (dataType) => {
  const now = Date.now();
  updateCacheMetadata({
    [dataType]: {
      lastSync: now,
      isStale: false
    }
  });
};

/**
 * Checks if cached data is stale based on expiry time or manual invalidation
 * @param {string} dataType - Type of data to check
 * @returns {boolean} True if cache is stale
 */
export const isCacheStale = (dataType) => {
  const metadata = getCacheMetadata();
  const dataMetadata = metadata[dataType];
  
  if (!dataMetadata || !dataMetadata.lastSync) {
    // No cache metadata means stale
    return true;
  }
  
  // Check if manually marked as stale
  if (dataMetadata.isStale === true) {
    return true;
  }
  
  // Check if stale due to age
  const now = Date.now();
  const cacheAge = now - dataMetadata.lastSync;
  const isStale = cacheAge > CACHE_EXPIRY_TIME;
  
  if (isStale) {
    // Cache is stale due to age
  }
  
  return isStale;
};

/**
 * Checks if app was reopened after being closed for more than 30 minutes
 * @returns {boolean} True if app needs fresh data
 */
export const needsFreshDataOnAppOpen = () => {
  const metadata = getCacheMetadata();
  const lastAppOpen = metadata.lastAppOpen;
  
  if (!lastAppOpen) {
    // First time opening app
    recordAppOpen();
    return true;
  }
  
  const now = Date.now();
  const timeSinceLastOpen = now - lastAppOpen;
  const needsFresh = timeSinceLastOpen > CACHE_EXPIRY_TIME;
  
  if (needsFresh) {
  }
  
  // Update last app open time
  recordAppOpen();
  
  return needsFresh;
};

/**
 * Records when the app was opened
 */
export const recordAppOpen = () => {
  updateCacheMetadata({
    lastAppOpen: Date.now()
  });
};

/**
 * Marks all cache as stale
 */
export const invalidateAllCache = () => {
  const metadata = getCacheMetadata();
  const updates = {};
  
  // Mark all data types as stale
  ['orders', 'clients', 'technicians', 'equipment', 'brands'].forEach(dataType => {
    if (metadata[dataType]) {
      updates[dataType] = {
        ...metadata[dataType],
        isStale: true
      };
    }
  });
  
  updateCacheMetadata(updates);
};

/**
 * Marks specific cache as stale
 * @param {string} dataType - Type of data to invalidate
 */
export const invalidateCache = (dataType) => {
  const metadata = getCacheMetadata();
  if (metadata[dataType]) {
    updateCacheMetadata({
      [dataType]: {
        ...metadata[dataType],
        isStale: true
      }
    });
  }
};

/**
 * Gets cache statistics for debugging
 * @returns {Object} Cache statistics
 */
export const getCacheStats = () => {
  const metadata = getCacheMetadata();
  const now = Date.now();
  const stats = {};
  
  ['orders', 'clients', 'technicians', 'equipment', 'brands'].forEach(dataType => {
    const dataMetadata = metadata[dataType];
    if (dataMetadata && dataMetadata.lastSync) {
      const ageMinutes = Math.round((now - dataMetadata.lastSync) / 1000 / 60);
      stats[dataType] = {
        lastSync: new Date(dataMetadata.lastSync).toISOString(),
        ageMinutes,
        isStale: isCacheStale(dataType)
      };
    } else {
      stats[dataType] = {
        lastSync: 'Never',
        ageMinutes: null,
        isStale: true
      };
    }
  });
  
  stats.lastAppOpen = metadata.lastAppOpen ? 
    new Date(metadata.lastAppOpen).toISOString() : 'Never';
  
  return stats;
};

/**
 * Clears all cache metadata
 */
export const clearCacheMetadata = () => {
  localStorage.removeItem(CACHE_METADATA_KEY);
};